import * as React from 'react';
import { useForm, type ControllerRenderProps } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { DEFAULT_COMPANY, type CompanySettings } from '@clawhire/shared';
import { useAuth } from '@/auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SkeletonLines } from '@/components/ui/skeleton';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { AdminNote, SettingsCard, SettingsSection } from '@/pages/settings/SettingsSection';
import { useSettingsQuery, useUpdateSettings } from '@/pages/settings/useSettings';
import logoHorizontalLight from '../../../../../libs/documents/assets/logo-horizontal-light.png';
import symbolViolet from '../../../../../libs/documents/assets/symbol-violet.png';

const hex = z
  .string()
  .trim()
  .regex(/^#?[0-9a-fA-F]{6}$/, 'Use a 6-digit hex colour, e.g. 7758F9')
  .transform((v) => v.replace('#', '').toUpperCase());

const email = z.string().trim().email('Enter a valid e-mail').or(z.literal(''));

const schema = z.object({
  name: z.string().trim().min(1, 'Required'),
  taglineEn: z.string().trim(),
  taglineDe: z.string().trim(),
  website: z.string().trim(),
  email,
  genericEmail: email,
  contactName: z.string().trim().min(1, 'Required'),
  contactRoleEn: z.string().trim(),
  contactRoleDe: z.string().trim(),
  contactEmail: email,
  contactPhone: z.string().trim(),
  font: z.string().trim().min(1, 'Required'),
  colors: z.object({
    black: hex,
    violet: hex,
    violetTint: hex,
    white: hex,
    textGrey: hex,
    lineGrey: hex,
    taglineGrey: hex,
  }),
  logoHorizontalLight: z.string().trim().min(1, 'Required'),
  symbolViolet: z.string().trim().min(1, 'Required'),
});
type FormInput = z.input<typeof schema>;
type FormOutput = z.output<typeof schema>;

type ColorKey = keyof CompanySettings['colors'];

const COLOR_FIELDS: Array<{ key: ColorKey; label: string; help: string }> = [
  { key: 'black', label: 'Ink', help: 'Headings and body text' },
  { key: 'violet', label: 'Violet', help: 'Primary accent' },
  { key: 'violetTint', label: 'Card grey', help: 'Fact cards, pills and the reference bar' },
  { key: 'white', label: 'Paper', help: 'Page background' },
  { key: 'textGrey', label: 'Text grey', help: 'Secondary text' },
  { key: 'lineGrey', label: 'Line grey', help: 'Rules and table lines' },
  { key: 'taglineGrey', label: 'Tagline grey', help: 'Tagline under the logo' },
];

function toValues(c: CompanySettings): FormInput {
  return {
    name: c.name,
    taglineEn: c.tagline.en,
    taglineDe: c.tagline.de,
    website: c.website,
    email: c.email,
    genericEmail: c.genericEmail,
    contactName: c.contact.name,
    contactRoleEn: c.contact.role.en,
    contactRoleDe: c.contact.role.de,
    contactEmail: c.contact.email,
    contactPhone: c.contact.phone,
    font: c.font,
    colors: { ...c.colors },
    logoHorizontalLight: c.logos.logoHorizontalLight,
    symbolViolet: c.logos.symbolViolet,
  };
}

function toCompany(v: FormOutput): CompanySettings {
  return {
    name: v.name,
    tagline: { en: v.taglineEn, de: v.taglineDe },
    website: v.website,
    email: v.email,
    genericEmail: v.genericEmail,
    contact: {
      name: v.contactName,
      role: { en: v.contactRoleEn, de: v.contactRoleDe },
      email: v.contactEmail,
      phone: v.contactPhone,
    },
    colors: v.colors,
    font: v.font,
    logos: { logoHorizontalLight: v.logoHorizontalLight, symbolViolet: v.symbolViolet },
  };
}

function toCss(hexValue: string): string {
  const v = hexValue.replace('#', '');
  return /^[0-9a-fA-F]{6}$/.test(v) ? `#${v}` : '#000000';
}

/** Brand (spec §3 company): names, taglines, contact, colours, font and logos used in generated documents. */
export function BrandSettings() {
  const { isAdmin } = useAuth();
  const settings = useSettingsQuery();
  const update = useUpdateSettings('Brand saved');

  const values = React.useMemo(
    () => (settings.data ? toValues(settings.data.company) : undefined),
    [settings.data],
  );
  const form = useForm<FormInput, unknown, FormOutput>({
    resolver: zodResolver(schema),
    defaultValues: toValues(DEFAULT_COMPANY),
    values,
    resetOptions: { keepDirtyValues: true },
  });

  const onSubmit = async (v: FormOutput) => {
    const saved = await update.mutateAsync({ company: toCompany(v) });
    form.reset(toValues(saved.company));
  };

  const text = (
    name: keyof Omit<FormInput, 'colors'>,
    label: string,
    help?: string,
    type: string = 'text',
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input type={type} {...field} />
          </FormControl>
          {help ? <FormDescription>{help}</FormDescription> : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <SettingsSection
      title="Brand"
      description="What the generated JDs, profiles and message signatures say about data-claw.de."
    >
      {isAdmin ? null : <AdminNote what="the brand" />}
      {settings.isLoading ? (
        <SkeletonLines lines={8} />
      ) : settings.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Settings could not be loaded: {settings.error.message}
        </p>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5" noValidate>
            <fieldset disabled={!isAdmin} className="space-y-5">
              <SettingsCard title="Company">
                <div className="grid gap-4 sm:grid-cols-2">
                  {text('name', 'Company name')}
                  {text('website', 'Website')}
                  {text('email', 'E-mail', 'Shown in document footers and signatures.', 'email')}
                  {text(
                    'genericEmail',
                    'Shared mailbox',
                    'Printed on documents instead of a name by users who hide their name (Account › Documents).',
                    'email',
                  )}
                  {text('font', 'Font', 'Must be installed where the DOCX is opened; the PDF embeds it.')}
                  <div className="sm:col-span-2">{text('taglineEn', 'Tagline (EN)')}</div>
                  <div className="sm:col-span-2">{text('taglineDe', 'Tagline (DE)')}</div>
                </div>
              </SettingsCard>

              <SettingsCard
                title="Contact"
                description="Signs the messages. Documents are signed by the user who generates them (name, title and e-mail from their account, or the contact person chosen under Account › Documents); this contact appears on documents only when there is no user behind the request, e.g. an agent token without an owner."
              >
                <div className="grid gap-4 sm:grid-cols-2">
                  {text('contactName', 'Name')}
                  {text('contactPhone', 'Phone', undefined, 'tel')}
                  {text('contactRoleEn', 'Role (EN)')}
                  {text('contactRoleDe', 'Role (DE)')}
                  <div className="sm:col-span-2">{text('contactEmail', 'E-mail', undefined, 'email')}</div>
                </div>
              </SettingsCard>

              <SettingsCard title="Colours" description="Hex values without the leading #.">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {COLOR_FIELDS.map((c) => (
                    <FormField
                      key={c.key}
                      control={form.control}
                      name={`colors.${c.key}`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{c.label}</FormLabel>
                          <FormControl>
                            <ColorInput field={field} label={c.label} />
                          </FormControl>
                          <FormDescription>{c.help}</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
              </SettingsCard>

              <SettingsCard
                title="Logos"
                description='The logos bundled with the app are used when a value is "bundled". To replace one, upload the PNG to a record via the Files panel and paste its File id here.'
              >
                <div className="grid gap-6 sm:grid-cols-2">
                  <figure className="space-y-3">
                    <div className="flex h-24 items-center justify-center rounded-md border bg-white p-3">
                      <img
                        src={logoHorizontalLight}
                        alt="Bundled horizontal logo (light)"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                    <figcaption className="text-xs text-muted-foreground">
                      Horizontal logo, light background (document header)
                    </figcaption>
                    {text('logoHorizontalLight', 'Horizontal logo', '"bundled" or a File id')}
                  </figure>
                  <figure className="space-y-3">
                    <div className="flex h-24 items-center justify-center rounded-md border bg-white p-3">
                      <img
                        src={symbolViolet}
                        alt="Bundled violet symbol"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                    <figcaption className="text-xs text-muted-foreground">
                      Symbol, violet (footer and small placements)
                    </figcaption>
                    {text('symbolViolet', 'Symbol', '"bundled" or a File id')}
                  </figure>
                </div>
              </SettingsCard>
            </fieldset>

            {isAdmin ? (
              <div className="flex flex-wrap items-center gap-2">
                <Button type="submit" loading={update.isPending} disabled={!form.formState.isDirty}>
                  Save brand
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => form.reset()}
                  disabled={!form.formState.isDirty}
                >
                  Discard
                </Button>
              </div>
            ) : null}
          </form>
        </Form>
      )}
    </SettingsSection>
  );
}

function ColorInput({
  field,
  label,
}: {
  field: ControllerRenderProps<FormInput, `colors.${ColorKey}`>;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={toCss(field.value)}
        onChange={(e) => field.onChange(e.target.value.slice(1).toUpperCase())}
        aria-label={`${label} swatch`}
        className="h-9 w-12 shrink-0 cursor-pointer rounded-md border border-input bg-background p-1"
      />
      <Input
        value={field.value}
        onChange={(e) => field.onChange(e.target.value)}
        onBlur={field.onBlur}
        name={field.name}
        ref={field.ref}
        maxLength={7}
        spellCheck={false}
        className="font-mono uppercase"
      />
    </div>
  );
}
